<script setup lang="ts">
const route = useRoute();
const { t } = useTexts();

useSeo({
  title: t('account.wallet.wallet'),
});

const { isUserV2Enabled } = useFeatureFlagsJson();
const { userCurrency, userCountry, userId } = useUserData();
const { isMobile, isLayoutMobileOrTablet } = useLayout();
const { resetCurrentMenuOpen } = useAccountMenuStore();
const { userCanOnlyWithdraw } = useUserLimits();

const { bonusWallet } = storeToRefs(useWalletStore());

const walletBonusWasExpired = computed<boolean>(() => {
  const now = new Date();
  const expireDatetime = new Date(bonusWallet.value.expiry_datetime);

  return expireDatetime.getTime() < now.getTime();
});

const walletHasBonusActive = computed<boolean>(() => {
  return !!bonusWallet.value.credit && !walletBonusWasExpired.value;
});

const activeCarouselLayout = computed<boolean>(() => {
  return (
    isLayoutMobileOrTablet.value &&
    !userCanOnlyWithdraw.value &&
    walletHasBonusActive.value
  );
});

const walletItems = ref(['real', 'bonus', 'cashback']);

onMounted(() => {
  // side effect visual do layout antigo — não roda com o Minha Conta V2
  if (isUserV2Enabled.value) {
    return;
  }

  if (route.query && route.query.select) {
    return;
  }

  resetCurrentMenuOpen();
});
</script>

<template>
  <UserV2ScreenWallet v-if="isUserV2Enabled" />
  <UserAccountPageLayout
    v-else
    :is-wallet-page="true"
  >
    <template v-if="!activeCarouselLayout">
      <div :class="$style.walletsContainer">
        <UserWalletRealWallet
          :user-country="userCountry"
          :user-currency="userCurrency"
          :user-id="userId"
        />

        <UserWalletBonusWallet
          v-if="!userCanOnlyWithdraw"
          :user-id="userId"
        />

        <UserWalletCashbackWallet
          v-if="!userCanOnlyWithdraw"
          :user-id="userId"
        />

        <div
          v-if="!userCanOnlyWithdraw"
          class="flex flex-col gap-4"
        >
          <NuxtLink to="/user/wallet/transactions?select=user-wallet">
            <div class="alert alert-info text-xs">
              {{ $t('account.wallet.transactions_alert_1') }}
              <strong>{{ $t('account.wallet.transactions_alert_2') }}</strong>
            </div>
          </NuxtLink>
        </div>
      </div>
    </template>

    <template v-else>
      <div :class="$style.walletsCarousel">
        <LazyWidgetsCarousel
          :display="{ spaceBetweenItems: [20, 10] }"
          :enable-navigation-buttons="!isMobile"
          item-class="w-auto"
          :items="walletItems"
          :navigation-always-visible="!isMobile"
          slider-fade="both"
        >
          <template #item="{ item }">
            <UserWalletRealWallet
              v-if="item === 'real'"
              :user-country="userCountry"
              :user-currency="userCurrency"
              :user-id="userId"
            />
            <UserWalletBonusWallet
              v-else-if="item === 'bonus' && !userCanOnlyWithdraw"
              :user-id="userId"
            />
            <UserWalletCashbackWallet
              v-else-if="item === 'cashback'"
              :user-country="userCountry"
              :user-currency="userCurrency"
              :user-id="userId"
            />
          </template>
        </LazyWidgetsCarousel>
        <div
          v-if="!userCanOnlyWithdraw"
          class="flex flex-col gap-4 mt-4"
        >
          <NuxtLink to="/user/wallet/transactions?select=user-wallet">
            <div class="alert alert-info text-2xs sm:text-xs flex flex-col">
              {{ $t('account.wallet.transactions_alert_1') }}
              <strong>{{ $t('account.wallet.transactions_alert_2') }}</strong>
            </div>
          </NuxtLink>
        </div>
      </div>
    </template>
  </UserAccountPageLayout>
</template>

<style module lang="scss">
.walletsContainer {
  @apply w-full flex flex-col gap-4 overflow-hidden;
}

.walletsCarousel {
  @apply w-full h-auto;
}
</style>
